import { exerciseField } from "../content/lianxi/exercise-fields.js";
import type { DrillProgress } from "../handwriting/xiezi-stages.js";

/**
 * One press posted to the `/user/lianxi/` handler, and the places its answer
 * carries.
 *
 * Apart from `lianxi-recorder.ts` and `lianxi-yufa-recorder.ts`, which decide
 * what to post about each pass or answer, so that the sending and the reading
 * of an answer are settled on their own.
 * A press that cannot reach the handler, or is refused by it, is answered with
 * nothing, and the recorder decides what the page does then.
 */

/** What the handler answers a press with, as far as the page reads it. */
export interface RecordAnswer {
  readonly commentSaved?: boolean;
  readonly commented?: boolean;
  readonly places?: readonly {
    readonly character: string;
    readonly stage: number;
    readonly pass: number;
  }[];

  /** A grammar exercise's items still waiting, in order, and the items missed. */
  readonly queue?: unknown;
  readonly practice?: unknown;
  readonly missed?: unknown;

  /** A reading Exercise's option chosen for each question, or -1. */
  readonly choices?: unknown;

  /** A reading Exercise's written replies, their tries and the unchecked (CB-77). */
  readonly replies?: unknown;
  readonly tries?: unknown;
  readonly unchecked?: unknown;
  readonly version?: unknown;
  readonly revision?: unknown;

  /** Whether the finish has handed the Exercise's material to 复习. */
  readonly handedOver?: boolean;

  /** How many cards it put in, and when the first comes due, once counted. */
  readonly handedOverCards?: unknown;
  readonly handedOverDueAt?: unknown;
  readonly marked?: unknown;
}

/**
 * Post one press as a form, and read the answer as JSON.
 * @param properties - Where, how, and the fields.
 * @returns The answer, or nothing where the handler could not be reached or
 * refused the press.
 */
export async function postPress(properties: {
  readonly fetch: typeof fetch;
  readonly address: string;
  readonly fields: Readonly<Record<string, string>>;
}): Promise<RecordAnswer | undefined> {
  try {
    const response = await properties.fetch(properties.address, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        "content-type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
      body: new URLSearchParams(properties.fields).toString(),
    });

    return response.ok ? ((await response.json()) as RecordAnswer) : undefined;
  } catch {
    return undefined;
  }
}

/** The presses on one Exercise, each naming it. */
export class ExercisePress {
  readonly #fetch: typeof fetch;
  readonly #address: string;
  readonly #slug: string;

  constructor(properties: {
    readonly fetch: typeof fetch;

    /** Where a press posts, as the block says. */
    readonly address: string;

    /** The Exercise's slug, which every post names. */
    readonly slug: string;
  }) {
    this.#fetch = properties.fetch;
    this.#address = properties.address;
    this.#slug = properties.slug;
  }

  /** One press, naming the Exercise. */
  async post(
    fields: Readonly<Record<string, string>>,
  ): Promise<RecordAnswer | undefined> {
    return postPress({
      fetch: this.#fetch,
      address: this.#address,
      fields: { [exerciseField]: this.#slug, ...fields },
    });
  }
}

/**
 * The places an answer carries, in the Exercise's order, or nothing where the
 * answer is short of a character's place.
 * @param answer - The handler's answer.
 * @param characters - The Exercise's characters, in order.
 * @returns Each character's place, with the pass at its first stroke.
 */
export function answeredPlaces(
  answer: RecordAnswer,
  characters: readonly string[],
): readonly DrillProgress[] | undefined {
  const places = characters.map((character) => {
    const found = answer.places?.find((place) => place.character === character);

    return found === undefined
      ? undefined
      : { stage: found.stage, pass: found.pass, stroke: 0 };
  });

  return places.every((place) => place !== undefined) ? places : undefined;
}
